"use client";

import { useMemo, useState, useSyncExternalStore } from "react";
import { FESTIVAL_POOL, FESTIVAL_QUESTIONS } from "./quiz-data";
import { rankFestivals, type AnswerMap } from "./scoring";
import { QuestionScreen } from "./QuestionScreen";
import { ResultCard } from "./ResultCard";
import { ShareRow } from "./ShareRow";
import { trackEvent } from "@/lib/analytics";

type Phase = "intro" | "question" | "results";

type SharedResult = { slug: string; pct: number };

type Props = { festivalJobSlugs: string[] };

const subscribeToStaticLocationSnapshot = () => () => {};
const getSharedResultSnapshot = (): SharedResult | null => {
  const params = new URLSearchParams(window.location.search);
  const r = params.get("r");
  const pct = Number(params.get("pct") ?? "0");
  return r && pct > 0 ? { slug: r, pct } : null;
};
const getNoSharedResultSnapshot = () => null;

export function FestivalQuizClient({ festivalJobSlugs }: Props) {
  const [phase, setPhase] = useState<Phase>("intro");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [sharedResultDismissed, setSharedResultDismissed] = useState(false);
  const urlSharedResult = useSyncExternalStore(
    subscribeToStaticLocationSnapshot,
    getSharedResultSnapshot,
    getNoSharedResultSnapshot
  );
  const sharedResult = sharedResultDismissed ? null : urlSharedResult;

  const jobSlugsSet = useMemo(
    () => new Set(festivalJobSlugs),
    [festivalJobSlugs]
  );

  const ranked = useMemo(() => {
    if (phase !== "results") return [];
    return rankFestivals(FESTIVAL_POOL, answers, FESTIVAL_QUESTIONS, jobSlugsSet);
  }, [phase, answers, jobSlugsSet]);

  const sharedFestival = sharedResult
    ? FESTIVAL_POOL.find((f) => f.slug === sharedResult.slug)
    : null;

  function start() {
    setPhase("question");
    trackEvent("festival_quiz_started");
  }

  function selectAnswer(answerId: string) {
    const currentQuestion = FESTIVAL_QUESTIONS[index];
    const answer = currentQuestion.answers.find((a) => a.id === answerId);
    if (!answer) return;

    const nextAnswers = { ...answers, [currentQuestion.id]: answer };
    setAnswers(nextAnswers);

    const isLast = index === FESTIVAL_QUESTIONS.length - 1;

    if (isLast) {
      trackEvent("festival_quiz_completed");
    }

    setTimeout(() => {
      if (isLast) {
        setPhase("results");
      } else {
        setIndex(index + 1);
      }
    }, 200);
  }

  function goBack() {
    if (index > 0) setIndex(index - 1);
  }

  function restart() {
    setPhase("intro");
    setIndex(0);
    setAnswers({});
    setSharedResultDismissed(true);
  }

  if (phase === "intro") {
    return (
      <section className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-[540px] mx-auto text-center">
          {sharedFestival && (
            <p className="text-[15px] text-gold font-medium mb-4">
              Someone got {sharedResult!.pct}% {sharedFestival.name}. Can you
              beat it?
            </p>
          )}
          <h1 className="font-serif text-[40px] md:text-[56px] text-navy leading-[1.04] mb-5">
            Which festival should you work at?
          </h1>
          <p className="text-[15px] md:text-[17px] text-text-muted leading-[1.6] mb-8">
            13 quick questions. Your top festival matches chosen for you, with a
            link to explore each one. 100% free.
          </p>
          <button
            onClick={start}
            className="bg-gold text-white px-8 py-3.5 rounded-sm text-[14px] font-semibold hover:bg-gold-light transition-colors"
          >
            Start the quiz &rarr;
          </button>
        </div>
      </section>
    );
  }

  if (phase === "question") {
    const currentQuestion = FESTIVAL_QUESTIONS[index];
    return (
      <QuestionScreen
        question={currentQuestion}
        questionIndex={index}
        totalQuestions={FESTIVAL_QUESTIONS.length}
        selectedAnswerId={answers[currentQuestion.id]?.id}
        onSelect={selectAnswer}
        onBack={goBack}
      />
    );
  }

  return (
    <section className="px-5 md:px-10 py-12 md:py-20">
      <div className="max-w-[640px] mx-auto">
        <h2 className="font-serif text-[32px] md:text-[40px] text-navy text-center mb-10">
          Your top matches
        </h2>
        <div className="space-y-6">
          {ranked.map((r, i) => (
            <ResultCard key={r.festival.slug} ranked={r} rank={i + 1} />
          ))}
        </div>
        {ranked.length > 0 && (
          <ShareRow
            festival={ranked[0].festival}
            matchPercent={ranked[0].matchPercent}
          />
        )}
        <div className="text-center mt-10">
          <button
            onClick={restart}
            className="text-[14px] text-text-muted underline hover:text-navy transition-colors"
          >
            Retake quiz
          </button>
        </div>
      </div>
    </section>
  );
}
